/* eslint-disable react/no-multi-comp */
import { Link } from "gatsby"
import React, { useEffect } from "react"
import { css, logTool, useSassUnitValues, listenDocEvent, keyboard } from "@jsUtils"

import { Eyebrow, Headline, GridLines, CircleButton, CTA } from "@shared"
import { CONSTANTS } from "@data"
import { PageSection } from "../PageSection"

// eslint-disable-next-line no-unused-vars
import Styles from "./news.module.scss"

const log = logTool(false)

const { NEWS, EVENTS, BREAKPOINTS } = CONSTANTS
const { MIN_SWIPE } = NEWS
const { KEYEVENT, RESIZE } = EVENTS
const MOBILE_BREAKPOINT = useSassUnitValues(BREAKPOINTS.MOBILE)

const { keyId } = keyboard

const NewsThumb = props => {
  const { color, story, state, style, delay, id, mobileLayout, ready } = props
  const { title, shortTitle, slug, date, source } = story
  log("news: mobileLayout?", mobileLayout)
  // const maxLength = mobileLayout ? MAX_TITLE_LENGTH_MOBILE : MAX_TITLE_LENGTH
  const formattedTitle = mobileLayout ? title : shortTitle || title
  return (
    <div
      id={id}
      style={{ transitionDelay: delay, ...style }}
      className={css(Styles.newsThumb, Styles[`state_${state}`])}
    >
      <div className={Styles.newsThumbContent} style={{ transitionDelay: delay }}>
        <div className={Styles.storyDate}>{date}</div>

        <div className={Styles.storyDate}>{source || "Exo"}</div>
        <Headline size={4} className={Styles.storyTitle}>
          <Link to={`${slug}`} className={Styles.storyTitleLink}>
            {" "}
            {formattedTitle}{" "}
          </Link>
        </Headline>
        <CTA
          ascope="CTAS"
          adata="NEWS.READMORE"
          ready={ready}
          content="chevron"
          color={color}
          to={`${slug}`}
          tabIndex={state !== "active" && state !== "current" ? "-1" : "0"}
        >
          Read More
        </CTA>
      </div>
    </div>
  )
}

/**
 * News - "The Latest from Exo"
 * @param {object} props - React props
 */
const News = props => {
  const { theme, color, stories } = props
  const [currentStory, setCurrentStory] = React.useState(0)
  const [mobileLayout, setMobileLayout] = React.useState(false)
  const [ready, setReady] = React.useState(false)

  const visibleStories = mobileLayout ? 2 : 3
  const thumbOffset = mobileLayout ? 100 / (visibleStories - 0.5) : 100 / visibleStories
  let lastTouchX = null

  const storiesLength =
    stories.length % visibleStories
      ? Math.ceil(stories.length / visibleStories) * visibleStories
      : stories.length

  const thumbs = []
  for (let i = 0; i < visibleStories * 3; i++) {
    let index = (currentStory + i - visibleStories) % storiesLength
    if (index < 0) {
      index += storiesLength
    } else if (index >= storiesLength) {
      index -= storiesLength
    }
    const story = stories[index]
    let state = i < visibleStories ? "shown" : i < visibleStories * 2 ? "active" : "waiting"
    state = i === visibleStories ? "current" : state
    if (story) {
      thumbs.push(
        <NewsThumb
          id={`news-thumb-${i + currentStory}`}
          ready={ready}
          key={i + currentStory}
          style={
            mobileLayout
              ? {
                  transform: `translateX(${100 * (i - visibleStories)}%)`
                }
              : { left: `${thumbOffset * (i % visibleStories)}%` }
          }
          delay={`${(i % visibleStories) * 0.03}s`}
          color={color}
          story={story}
          mobileLayout={mobileLayout}
          state={state}
        />
      )
    }
  }

  const previousStories = () => {
    const newStory = mobileLayout
      ? currentStory - visibleStories + 1
      : currentStory - visibleStories
    setCurrentStory(newStory)
  }

  const nextStories = () => {
    const newStory = mobileLayout
      ? currentStory + visibleStories - 1
      : currentStory + visibleStories
    setCurrentStory(newStory)
  }

  const onTouchStart = e => {
    if (e.touches && e.touches.length > 0) {
      lastTouchX = e.touches[0].clientX
    }
  }

  const onTouchEnd = e => {
    if (lastTouchX !== null) {
      const swipeDelta = lastTouchX - e.changedTouches[0].clientX
      if (Math.abs(swipeDelta) > MIN_SWIPE) {
        if (swipeDelta > 0) {
          nextStories()
        } else {
          previousStories()
        }
      }
    }
  }

  const onkeyboard = e => {
    const keypress = keyId(e)
    if (keypress.left) {
      previousStories()
    }
    if (keypress.right) {
      previousStories()
    }
  }

  const onResize = () => {
    if (window.innerWidth < MOBILE_BREAKPOINT && !mobileLayout) {
      setMobileLayout(true)
      setCurrentStory(0)
    } else if (window.innerWidth >= MOBILE_BREAKPOINT && mobileLayout) {
      setMobileLayout(false)
      setCurrentStory(0)
    }
  }

  useEffect(() => {
    onResize()
    listenDocEvent(RESIZE, onResize)
    listenDocEvent(KEYEVENT, onkeyboard)
    return () => {
      listenDocEvent(RESIZE, onResize, false)
      listenDocEvent(RESIZE, onkeyboard, false)
    }
  })

  return (
    <PageSection
      className={css(Styles.section, ready && Styles.ready)}
      theme={theme}
      ready={ready}
      onReady={() => {
        setReady(true)
      }}
    >
      <GridLines theme={theme} />
      <div className={Styles.wrapper}>
        <Eyebrow className={Styles.eyebrow} color={color}>
          News
        </Eyebrow>
        <div className={Styles.header}>
          <Headline size={1} className={Styles.headline}>
            The latest from Exo
          </Headline>
          <div className={Styles.buttons}>
            <CircleButton
              className={Styles.backButton}
              content="chevron"
              flipped="true"
              onClick={previousStories}
            />
            <CircleButton
              className={Styles.forwardButton}
              content="chevron"
              onClick={nextStories}
            />
          </div>
        </div>
        <div className={Styles.stories} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {thumbs}
        </div>
      </div>
    </PageSection>
  )
}

export default News
